/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import {
    Blank,
    Effect,
    FullAssignedTechnicianEntry,
    InventoryItem,
    Nullable,
    Pagination,
    Setter,
    TechnicianEntryEgressInventory,
} from "../../utils/Interfaces";
import { customErrHandlerFactory, getHandler, postHandler, stdErrHandlerFactory } from "../../utils/Requests";
import { AxiosError, AxiosResponse } from "axios";

export function updateTechnicianEntry(
    techEntryId: number,
    dispatch: Dispatch<AnyAction>,
    setTechnicianEntry: Setter<FullAssignedTechnicianEntry>,
    setTicketErrorMessage: Setter<string>,
    setLoading?: Setter<boolean>
) {
    getHandler<FullAssignedTechnicianEntry>(
        `/api/tech-entries/details/${techEntryId}`,
        dispatch,
        (response: AxiosResponse<FullAssignedTechnicianEntry, any>) => setTechnicianEntry(response.data),
        customErrHandlerFactory(dispatch, (error: AxiosError) => {
            if (error.response && error.response.status === 403) {
                setTicketErrorMessage("You do not have access to the requested ticket.");
            } else if (error.response && error.response.status === 404) {
                setTicketErrorMessage("The requested assignment is unavailable.");
            } else {
                setTicketErrorMessage("An error has occurred, please report this to the Techwise Portal administrator.");
            }
        }),
        setLoading
    );
}

export function updateTechnicianWorkOrderHistory(
    workOrderId: number,
    tripNo: number,
    dispatch: Dispatch<AnyAction>,
    setWorkOrderHistory: Setter<FullAssignedTechnicianEntry[]>,
    setLoading: Setter<boolean>
) {
    getHandler<Pagination<FullAssignedTechnicianEntry>>(
        `/api/work-orders/history/${workOrderId}/${tripNo}`,
        dispatch,
        (response: AxiosResponse<Pagination<FullAssignedTechnicianEntry>, any>) => setWorkOrderHistory(response.data.results),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function submitTechnicianEntry(
    technicianEntryId: number,
    dispatch: Dispatch<AnyAction>,
    onTechnicianEntrySubmitted: Effect,
    setLoading?: Setter<boolean>
) {
    postHandler<Blank, Blank>(
        `/api/tech-entries/submit/${technicianEntryId}`,
        {},
        dispatch,
        (response: AxiosResponse<Blank, any>) => onTechnicianEntrySubmitted(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function submitTechnicianEntryV2(
    technicianEntryId: number,
    dispatch: Dispatch<AnyAction>,
    onTechnicianEntrySubmitted: Effect,
    setLoading?: Setter<boolean>
) {
    postHandler<Blank, Blank>(
        `/api/tech-entries/submit-v2/${technicianEntryId}`,
        {},
        dispatch,
        (response: AxiosResponse<Blank, any>) => onTechnicianEntrySubmitted(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function updateTechnicianChecklistItem(
    checklistItemId: number,
    dispatch: Dispatch<AnyAction>,
    status: number,
    onStatusUpdated: Effect,
    setLoading?: Setter<boolean>
) {
    postHandler<Blank, Blank>(
        `/api/tech-entries/checklist/update/${checklistItemId}/${status}`,
        {},
        dispatch,
        (response) => onStatusUpdated(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function setTechnicianEntryEgressForm(
    technicianEntryId: number,
    inventoryId: number,
    amount: number,
    notes: Nullable<string>,
    dispatch: Dispatch<AnyAction>,
    onTechnicianEntryEgressFormSet: Effect
) {
    postHandler<Blank, Blank>(
        `/api/inventory/set-egress/${technicianEntryId}/${inventoryId}/${amount}`,
        { notes: notes },
        dispatch,
        (response) => onTechnicianEntryEgressFormSet(),
        stdErrHandlerFactory(dispatch),
        () => {}
    );
}

export function getMyInventories(
    dispatch: Dispatch<AnyAction>,
    setTechnicianEntryEgresses: Setter<InventoryItem[]>,
    setLoading: Setter<boolean> = () => {}
) {
    getHandler<Pagination<InventoryItem>>(
        "/api/inventory/my-inventories",
        dispatch,
        (response: AxiosResponse<Pagination<InventoryItem>, any>) => setTechnicianEntryEgresses(response.data.results),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function getInventoriesForTechnicianEntry(
    technicianEntryId: number,
    dispatch: Dispatch<AnyAction>,
    setTechnicianEntryEgresses: Setter<TechnicianEntryEgressInventory[]>
) {
    getHandler<Pagination<TechnicianEntryEgressInventory>>(
        `/api/inventory/get-inventories-for-tech-entry/${technicianEntryId}`,
        dispatch,
        (response: AxiosResponse<Pagination<TechnicianEntryEgressInventory>, any>) =>
            setTechnicianEntryEgresses(response.data.results),
        stdErrHandlerFactory(dispatch),
        () => {}
    );
}
